import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const DUR = 5200; // ms per scene

const SCENES = [
  { title: 'Create your profile', text: 'Add your skills, experience and city in minutes.' },
  { title: 'Get matched', text: 'See jobs ranked by how well they fit you.' },
  { title: 'Apply in one click', text: 'No long forms. Your profile does the work.' },
  { title: 'Get hired', text: 'Track interviews and offers in one place.' },
];

function Ring({ pct, size = 56, delay = 0, label }) {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="5" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#34D399" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={c} className="xp-ring" style={{ '--c': c, '--o': c * (1 - pct / 100), animationDelay: `${delay}ms` }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-extrabold text-white">{label ?? `${pct}%`}</span>
    </div>
  );
}

const Card = ({ children, className = '', style }) => (
  <div className={`rounded-2xl bg-white/10 backdrop-blur border border-white/15 shadow-xl ${className}`} style={style}>{children}</div>
);

function SceneProfile() {
  return (
    <Card className="xp-pop w-[88%] max-w-md p-4 sm:p-5">
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/20 flex items-center justify-center text-white">
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="3.4" /><path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" /></svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="xp-type text-sm sm:text-base font-bold text-white" style={{ '--w': '9ch' }}>Puneet Kumar</p>
          <p className="xp-type text-xs text-white/70" style={{ '--w': '16ch', animationDelay: '.9s' }}>Frontend Developer · Bengaluru</p>
        </div>
        <Ring pct={100} delay={400} />
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {['React', 'JavaScript', 'Tailwind', 'Excel'].map((s, i) => (
          <span key={s} className="xp-chip px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold" style={{ animationDelay: `${1.4 + i * 0.35}s` }}>{s}</span>
        ))}
      </div>
      <p className="xp-fade mt-4 text-xs font-semibold text-emerald-300" style={{ animationDelay: '3s' }}>✓ Profile complete</p>
    </Card>
  );
}

function SceneMatch() {
  const jobs = [
    { t: 'Full Stack Developer', c: 'Cognizant', p: 92 },
    { t: 'Software Engineer', c: 'Wipro', p: 87 },
    { t: 'Frontend Developer', c: 'TCS', p: 78 },
  ];
  return (
    <div className="w-[90%] max-w-md space-y-2.5">
      {jobs.map((j, i) => (
        <Card key={j.t} className="xp-slide flex items-center gap-3 px-4 py-3" style={{ animationDelay: `${i * 0.5}s` }}>
          <span className="w-10 h-10 rounded-xl bg-white text-[#7C3AED] font-extrabold flex items-center justify-center">{j.c[0]}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white truncate">{j.t}</p>
            <p className="text-xs text-white/70">{j.c} · Bengaluru</p>
          </div>
          <Ring pct={j.p} size={50} delay={i * 500 + 400} />
        </Card>
      ))}
    </div>
  );
}

function SceneApply() {
  return (
    <Card className="xp-pop relative w-[88%] max-w-md p-5">
      <p className="text-white font-bold">Frontend Developer</p>
      <p className="text-xs text-white/70 mb-1">TCS · Bengaluru</p>
      <p className="text-xs font-semibold text-emerald-300 mb-5">₹20K – 40K / month</p>
      <div className="relative h-10">
        <button type="button" tabIndex={-1} className="xp-btn absolute right-0 top-0 h-10 w-36 rounded-xl brand-gradient text-white text-sm font-bold overflow-hidden">
          <span className="xp-apply absolute inset-0 flex items-center justify-center">Apply now</span>
          <span className="xp-applied absolute inset-0 flex items-center justify-center bg-emerald-500">Applied ✓</span>
        </button>
        <span className="xp-ripple absolute right-[4.2rem] top-1/2 w-6 h-6 -mt-3 rounded-full border-2 border-white/80" />
        <svg viewBox="0 0 24 24" className="xp-cursor absolute right-[4rem] top-3 w-6 h-6 drop-shadow-lg" fill="white" stroke="#1E0F3D" strokeWidth="1.2"><path d="M4 3l16 8-7 1.5L11 20 4 3z" /></svg>
      </div>
    </Card>
  );
}

function SceneHired() {
  return (
    <div className="relative flex items-center justify-center w-full h-full">
      {Array.from({ length: 22 }).map((_, i) => (
        <span key={i} className="xp-confetti absolute top-0 w-2 h-3 rounded-sm"
          style={{ left: `${(i * 37) % 100}%`, background: ['#34D399', '#F472B6', '#FBBF24', '#A78BFA'][i % 4], animationDelay: `${1 + (i % 7) * 0.12}s` }} />
      ))}
      <Card className="xp-pop w-[80%] max-w-sm p-6 text-center">
        <svg viewBox="0 0 52 52" className="w-16 h-16 mx-auto mb-3">
          <circle cx="26" cy="26" r="23" fill="none" stroke="#34D399" strokeWidth="3" className="xp-draw" style={{ '--l': 145 }} />
          <path d="M15 27l8 8 14-16" fill="none" stroke="#34D399" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="xp-draw" style={{ '--l': 40, animationDelay: '.7s' }} />
        </svg>
        <p className="text-lg font-extrabold text-white">Offer received!</p>
        <p className="text-xs text-white/75 mt-1">Welcome aboard. Your next move starts now.</p>
      </Card>
    </div>
  );
}

const VIEWS = [SceneProfile, SceneMatch, SceneApply, SceneHired];

export default function PlanBExplainer() {
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(!reduced);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const running = playing && inView;
  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => setScene((s) => (s + 1) % SCENES.length), DUR);
    return () => clearTimeout(t);
  }, [running, scene]);

  const View = VIEWS[scene];

  return (
    <section ref={ref} id="watch" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
      <style>{`
        .xp-stage{background:radial-gradient(circle at 20% 15%,#6D28D9,transparent 55%),radial-gradient(circle at 85% 90%,#BE185D,transparent 55%),#1E0F3D}
        @keyframes xp-pop{from{opacity:0;transform:scale(.85) translateY(16px)}to{opacity:1;transform:none}}
        @keyframes xp-slide{from{opacity:0;transform:translateX(60px)}to{opacity:1;transform:none}}
        @keyframes xp-fade{from{opacity:0}to{opacity:1}}
        @keyframes xp-chip{from{opacity:0;transform:scale(.4)}60%{transform:scale(1.15)}to{opacity:1;transform:scale(1)}}
        @keyframes xp-ring{from{stroke-dashoffset:var(--c)}to{stroke-dashoffset:var(--o)}}
        @keyframes xp-type{from{width:0}to{width:var(--w)}}
        @keyframes xp-bar{from{transform:scaleX(0)}to{transform:scaleX(1)}}
        @keyframes xp-cursor{0%{transform:translate(110px,80px);opacity:0}15%{opacity:1}60%,100%{transform:translate(0,0);opacity:1}}
        @keyframes xp-click{0%,100%{transform:scale(1)}50%{transform:scale(.92)}}
        @keyframes xp-ripple{from{transform:scale(.4);opacity:.9}to{transform:scale(3);opacity:0}}
        @keyframes xp-out{to{opacity:0;transform:translateY(-10px)}}
        @keyframes xp-in{from{opacity:0}to{opacity:1}}
        @keyframes xp-fall{from{transform:translateY(-10px) rotate(0);opacity:1}to{transform:translateY(330px) rotate(540deg);opacity:0}}
        @keyframes xp-draw{from{stroke-dashoffset:var(--l)}to{stroke-dashoffset:0}}
        .xp-pop{animation:xp-pop .7s cubic-bezier(.2,.8,.2,1) both}
        .xp-slide{opacity:0;animation:xp-slide .7s cubic-bezier(.2,.8,.2,1) forwards}
        .xp-fade{opacity:0;animation:xp-fade .6s ease forwards}
        .xp-chip{opacity:0;animation:xp-chip .5s ease forwards}
        .xp-ring{stroke-dashoffset:var(--c);animation:xp-ring 1.3s ease-out forwards}
        .xp-type{overflow:hidden;white-space:nowrap;width:0;animation:xp-type .8s steps(16) .3s forwards}
        .xp-bar{transform-origin:left;transform:scaleX(0);animation:xp-bar ${DUR}ms linear forwards}
        .xp-cursor{animation:xp-cursor 1.6s .6s cubic-bezier(.3,.7,.2,1) both}
        .xp-btn{animation:xp-click .3s 2.2s}
        .xp-ripple{opacity:0;animation:xp-ripple .7s 2.2s ease-out}
        .xp-apply{animation:xp-out .25s 2.3s forwards}
        .xp-applied{opacity:0;animation:xp-in .3s 2.35s forwards}
        .xp-confetti{opacity:0;animation:xp-fall 2.6s ease-in forwards}
        .xp-draw{stroke-dasharray:var(--l);stroke-dashoffset:var(--l);animation:xp-draw .7s .2s ease forwards}
        .xp-paused .xp-bar,.xp-paused *{animation-play-state:paused!important}
        @media (prefers-reduced-motion:reduce){.xp-stage *{animation-duration:.01s!important;animation-delay:0s!important}}
      `}</style>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.35fr] gap-10 lg:gap-14 items-center">
        <div>
          <p className="inline-block text-xs font-semibold text-[#7C3AED] bg-[#F5F0FA] rounded-full px-3 py-1.5 mb-5">See it in action</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">How PlanB Jobs works</h2>
          <p className="text-gray-500 leading-relaxed mb-6 max-w-md">From profile to offer letter in four simple steps.</p>
          <ol className="space-y-2">
            {SCENES.map((s, i) => (
              <li key={s.title}>
                <button type="button" onClick={() => { setScene(i); setPlaying(true); }}
                  className={`w-full text-left flex items-start gap-3 rounded-xl px-3 py-2.5 transition ${i === scene ? 'bg-[#F5F0FA]' : 'hover:bg-[#FBF5FF]'}`}>
                  <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 transition ${i === scene ? 'brand-gradient text-white' : 'bg-white border border-purple-100 text-[#7C3AED]'}`}>{i + 1}</span>
                  <span>
                    <span className={`block text-sm font-bold ${i === scene ? 'text-[#1E0F3D]' : 'text-gray-500'}`}>{s.title}</span>
                    {i === scene && <span className="block text-xs text-gray-500 mt-0.5">{s.text}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <Link to="/register" className="shine-btn brand-gradient text-white inline-block mt-6 px-8 py-3.5 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)]">Create free profile</Link>
        </div>

        <div className="rounded-[1.75rem] p-[3px] overflow-hidden relative shadow-[0_40px_90px_-30px_rgba(124,58,237,0.6)]">
          <div className="spin-border" aria-hidden="true" />
          <div className={`xp-stage ${running ? '' : 'xp-paused'} relative rounded-[1.6rem] overflow-hidden aspect-video`}>
            <div className="absolute top-3 left-4 flex items-center gap-2 z-10">
              <span className="w-6 h-6 rounded-full bg-white text-[#E11D2E] text-xs font-extrabold flex items-center justify-center">B</span>
              <span className="text-xs font-bold text-white/90">PlanB Jobs</span>
            </div>
            <span className="absolute top-3 right-4 text-[11px] font-semibold text-white/70 z-10">{scene + 1} / {SCENES.length}</span>

            <div key={scene} className="absolute inset-0 flex items-center justify-center pt-6 pb-10"><View /></div>

            <div className="absolute bottom-0 inset-x-0 px-4 pb-3 z-10">
              <p className="text-center text-sm font-bold text-white mb-2">{SCENES[scene].title}</p>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setPlaying((p) => !p)} aria-label={playing ? 'Pause' : 'Play'}
                  className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center shrink-0">
                  {playing ? <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
                    : <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>}
                </button>
                <div className="flex gap-1.5 flex-1">
                  {SCENES.map((_, i) => (
                    <span key={i} className="h-1 flex-1 rounded-full bg-white/20 overflow-hidden">
                      {i < scene && <span className="block h-full bg-white" />}
                      {i === scene && <span key={scene} className="xp-bar block h-full bg-white" />}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}